import { useAppDispatch } from "@/app/hooks";
import {
  setCurrentSurveyGroup,
  setError,
} from "@/features/auth/interactiveSurveySlice";
import { clearActiveSurveyId } from "@/services/interactiveSurveyAuth";
import { useGetSurveyGroupWithSurveysQuery } from "@/services/surveyGroupService";
import { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router";
import LoadingSpinner from "./LoadingSpinner";

/**
 * Interactive Survey Group Layout
 * Fetches survey-group + member survey data once for all group-scoped child routes
 */
const InteractiveSurveyGroupLayout = () => {
  const { surveyGroupId } = useParams<{ surveyGroupId: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data,
    error,
    isLoading,
    isFetching,
  } = useGetSurveyGroupWithSurveysQuery(surveyGroupId!, {
    skip: !surveyGroupId,
    // Shortlist toggles happen outside this query's cache; refresh on entry
    refetchOnMountOrArgChange: true,
  });

  // A prior visit to a member survey may have left its ID set as the active
  // surveyId header — clear it so group-scoped requests use surveyGroupId.
  useEffect(() => {
    clearActiveSurveyId();
  }, []);

  useEffect(() => {
    // Skip the cached copy while refetching so stale shortlist flags never
    // overwrite the store.
    if (data && !isFetching) {
      dispatch(setCurrentSurveyGroup(data));
    }
  }, [data, isFetching, dispatch]);

  useEffect(() => {
    if (error && surveyGroupId && !location.pathname.includes("/login")) {
      dispatch(setError("Failed to load survey group details"));
      navigate(`/interactive/group/${surveyGroupId}/login`);
    }
  }, [error, dispatch, navigate, surveyGroupId, location.pathname]);

  if (isLoading || isFetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading survey group..." />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <Outlet />
    </main>
  );
};

export default InteractiveSurveyGroupLayout;
